<template>
    <filters-wrapper :has-active-filters="props.hasActiveFilters" @submit="emit('submit')" @reset="resetFilter">
        <template v-slot:panel>
            <FilterStatusToggle v-model="filters.active" @update:model-value="emit('submit')" />
        </template>
        <v-form class="table-filters">
            <v-row dense>
                <v-col cols="12" md="3">
                    <v-text-field
                        v-model="filters.name"
                        :label="$t('name')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="name"
                        class="mb-4"
                    />
                    <v-text-field
                        v-model="filters.supplierName"
                        :label="$t('name_for_supplier')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="name-for-supplier"
                        class="mb-4"
                    />
                    <v-autocomplete
                        v-model="filters.supplierId"
                        variant="outlined"
                        density="compact"
                        color="primary"
                        data-cy="supplier"
                        :items="supplierStore.internalSuppliers"
                        item-title="name"
                        item-value="id"
                        clearable
                        hide-details
                        :label="$t('filterTitles.supplierName')"
                        class="mb-4"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        ref="option"
                        v-model="filters.countryId"
                        :items="useCountries"
                        item-title="title"
                        item-value="value"
                        :label="$t('country')"
                        closable-chips
                        hide-details
                        clearable
                        density="compact"
                        color="primary"
                        data-cy="country-id"
                        class="mb-4"
                        @update:model-value="updateCities()"
                    />
                    <v-autocomplete
                        ref="cityRef"
                        v-model="filters.cityId"
                        :items="cities"
                        item-title="title"
                        item-value="value"
                        :label="$t('city')"
                        :loading="isCitiesLoading"
                        :disabled="isCitiesLoading || !cities.length"
                        closable-chips
                        hide-details
                        clearable
                        density="compact"
                        color="primary"
                        data-cy="city-id"
                        class="mb-4"
                    />
                    <v-text-field
                        v-model="filters.internalDescription"
                        :label="$t('description_for_internal_use')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="internal-description"
                        class="mb-4"
                    />
                </v-col>
                <v-col v-if="useIsShowPublishing" cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.stage"
                        :items="useContractStage"
                        :label="$t('stage')"
                        clearable
                        density="compact"
                        hide-details
                        multiple
                        data-cy="stage"
                        class="mb-4"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <DepartmentSelect v-model="filters.departmentId" density="compact" />
                </v-col>
            </v-row>
        </v-form>
    </filters-wrapper>
</template>

<script setup lang="ts">
    import type {SelectOptionType} from '~/utils/types/types'
    import {useContractStage, useIsShowPublishing, useCountries} from '~/composables/form-helpers'
    import type {AccommodationFilterType} from '~/utils/types/account/products/types'
    import DepartmentSelect from '~/components/account/snippets/DepartmentSelect.vue'

    const props = defineProps<{
        hasActiveFilters: boolean
    }>()

    const filters = defineModel<AccommodationFilterType>()
    const emit = defineEmits<{
        (e: 'submit' | 'reset'): void
    }>()

    const isCitiesLoading = ref<boolean>(false)
    const cities = ref<SelectOptionType[]>([])
    const cityRef = ref()

    const app = useNuxtApp()

    watch(useCountries, async () => {
        if (filters.value.countryId) await loadCities()
    })

    const updateCities = async (): Promise<void> => {
        cities.value = []
        if (filters.value.cityId) cityRef.value.reset()
        if (filters.value.countryId) await loadCities()
    }

    async function loadCities() {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: filters.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const resetFilter = (): void => {
        cities.value = []
        emit('reset')
    }
</script>
